import React, { forwardRef, useState, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled/macro';
import { motion } from 'framer-motion';

import './Card.css';
import { Icon } from '../common/Icon';
import Tooltip from '../common/Tooltip';
import CountCircle from '../common/CountCircle';
import RankingIndicator from '../ranking/RankingIndicator';
import RankChangeIndicator from '../ranking/RankChangeIndicator';
import NewCauseDot from '../common/NewCauseDot';
import { scaleOrdinal } from '@visx/scale';

const cardLayout = {
  observation: { columns: 'auto', areas: 'card' },
  consequence: { columns: 'max-content auto', areas: 'count card' },
  cause: {
    columns: 'auto max-content',
    areas: 'card count',
    iconColor: 'var(--color-root-causes)',
  },
};

const StateSvgName = {
  target: 'cra-target',
  hazard: 'cra-hazard',
  breach: 'cra-breach',
  high: 'cra-high',
  low: 'cra-low',
  normal: 'cra-neutral',
  sensor: 'cra-sensor',
  mcoHigh: 'cra-mco-high',
  mcoLow: 'cra-mco-low',
};

const StyledCardWrapper = styled.div`
  font-size: 0.78rem;
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-gap: 0.5rem;
  grid-template-columns: ${({ type }) => cardLayout[type].columns};
  grid-template-areas: '${({ type }) => cardLayout[type].areas}';
  --card-icon-color: ${({ type }) => cardLayout[type].iconColor};
`;

const Card = forwardRef(
  (
    {
      data,
      label,
      isSelected,
      isExpanded,
      onCardClick,
      disabled,
      hovered,
      count,
      onMouseEnter,
      onMouseLeave,
      id,
      type,
      rankChange,
      showDot,
      isFlagged,
      flagComment,
    },
    ref
  ) => {
    const { description, state, isSensor, rank, relativeRank, concept } = data;
    const [arrowActive, setArrowActive] = useState(false);
    const handleArrowShow = useCallback(() => setArrowActive(true), []);
    const handleArrowDismiss = useCallback(() => setArrowActive(false), []);

    const iconByConceptAndState =
      concept === 'mco' || concept === 'dco' || concept === 'sup'
        ? state === 'high'
          ? 'mcoHigh'
          : 'mcoLow'
        : state;

    const dotVisible = type === 'cause' && showDot && !arrowActive;

    return (
      <button
        className={'button-wrapper'}
        type="button"
        onClick={onCardClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled}
        data-id={id}
        ref={ref}
      >
        <StyledCardWrapper type={type}>
          {type === 'cause' && (
            <div className="rank-change-indicator">
              <RankChangeIndicator
                change={rankChange}
                onShow={handleArrowShow}
                onDismiss={handleArrowDismiss}
              />
              <NewCauseDot visible={dotVisible} />
              {/* {isFlagged && (
                <Tooltip label="Attention!" message={flagComment} type="critical">
                  <svg
                    style={{
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                      marginLeft: '0.5rem',
                      alignSelf: 'center',
                    }}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" fill="#ff3b30" />
                  </svg>
                </Tooltip>
              )} */}
            </div>
          )}
          <Tooltip
            label={label}
            message={description}
            note={isFlagged ? flagComment : undefined}
            type={isFlagged ? 'warning' : 'normal'}
            className="card-tooltip-target"
          >
            <div
              className={`card ${isSelected ? 'card-selected' : 'card-unselected'} ${
                isExpanded ? 'card-expanded' : ''
              } ${hovered ? 'card-hovered' : ''} ${isFlagged ? 'card-flagged' : ''}`}
            >
              <div className="card-state">
                <Icon
                  name={StateSvgName[iconByConceptAndState]}
                  className="card-state__icon"
                  height="40"
                  width="40"
                  fill="currentColor"
                />
              </div>
              <div className="card-body text-ellipsis">
                <span className="text-bold text-ellipsis">{label}</span>
                <div className="text-small color-gray">
                  <div className="text-ellipsis">{description}</div>
                </div>
              </div>
              <div className="card-rank">
                {isFlagged && (
                  <svg
                    className="card-flag"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"
                      fill="var(--color-flagged)"
                    />
                  </svg>
                )}
                <RankingIndicator rank={relativeRank} height="40" width="40" />
              </div>
            </div>
          </Tooltip>
        </StyledCardWrapper>
      </button>
    );
  }
);

Card.displayName = 'Card';
export default motion(Card);
